import {
  CmSheetEventEnum,
  CmSheetStateEnum,
  VerificationStatusEnum,
} from "../../constants/AppConstants";
import { LanguageLevelEnum } from "../../constants/LanguageConstants";
import { CollaboratorInfo, Role } from "../AppTypes";
import { Certification, HistoryCertification } from "./certifications.type";
import { Course, HistoryCourse } from "./courses.type";
import { BatchOperation, PaginatedBody } from "./generic.type";
import { CollaboratorHistoryProject } from "./projects.type";
import { TechSkill } from "./techSkills.type";

export interface Collaborator extends CollaboratorInfo {
  cmSheetStatus: string | null;
  hasDetailsVisibility: boolean;
  verificationStatus: VerificationStatusEnum;
}

export type GetCollaboratorsResponse = PaginatedBody<Collaborator>;

export type CollaboratorCM = {
  id: number;
  name: string;
  surname: string;
  startDate: string;
  endDate: string;
};

export interface CollaboratorDetails {
  id: number;
  name: string;
  surname: string;
  area: string | null;
  practice: string | null;
  cc: string | null;
  cmSheetStatus: string | null;
  taxIdCode: string;
  companyHeadquarter: string;
  internalGrade: string | null;
  contractType: string;
  workingTime: string;
  ccnlLevel: string;
  hireDate: string;
  terminationDate: string | null;
  email: string;
}

export type GetCollaboratorImageResponse = { image: string | null };

export interface CollaboratorCmSheet {
  id: number;
  configYear: number;
  isCurrent: boolean;
  careerManagerId: number | null;
}

export type CollaboratorTrainingCourse = {
  completed: number;
  total: number;
};

export type CollaboratorTrainingCourses = {
  language: CollaboratorTrainingCourse;
  tech: CollaboratorTrainingCourse;
  path: CollaboratorTrainingCourse;
  formation: CollaboratorTrainingCourse;
};

export type CollaboratorTraining = {
  year: number;
  progress: number;
  courses: CollaboratorTrainingCourses;
};

export type CollaboratorAchievement = {
  year: number;
  percentageGoal: number | null;
  percentualTrend: number | null;
};

export type Experience = {
  totalExperienceMonths: number | null;
  companyExperienceMonths: number | null;
};

export type CollaboratorAnagraphic = {
  name: string;
  surname: string;
  email: string;
  experience: Experience;
};

export interface CollaboratorAdvancement {
  anagraphic: CollaboratorAnagraphic;
  achievements: CollaboratorAchievement[];
  training: CollaboratorTraining[];
}

export type GetCollaboratorAdvancementResponse = CollaboratorAdvancement;

export type GetCollaboratorDetailsResponse = CollaboratorDetails;

export type GetCollaboratorCmSheetsResponse = CollaboratorCmSheet[];

export type GetCollaboratorCarrerManagersResponse = CollaboratorCM[];

export type GetCollaboratorsTechSkillsResponse = PaginatedBody<TechSkill>;

export type GetCollaboratorsHistoryProjectsResponse =
  PaginatedBody<CollaboratorHistoryProject>;

export type InternalGrade = {
  jobFamilyListId: number[];
  gradeId: number | null;
  pathId: number | null;
  pathGradeId: number | null;
  subgradeId: number | null;
};

export type GetCollaboratorCmSheetInternalGradeResponse = {
  start: InternalGrade | null;
  end: InternalGrade | null;
};

export type UpdateCollaboratorCmSheetInternalGradeRequest = {
  start: InternalGrade;
  end: InternalGrade;
};

export type CollaboratorCmSheetCourse = {
  id: number;
  course: Course;
  isFinished: boolean;
  fileName: string;
};

export type GetCollaboratorsCmSheetCoursesResponse =
  CollaboratorCmSheetCourse[];

export type GetCmSheetAvailableCoursesResponse = PaginatedBody<Course>;

export type UpdateCollaboratorsCmSheetCourseItem = {
  courseId: number;
  isFinished: boolean;
};
export type UpdateCollaboratorsCmSheetCourseRequest =
  BatchOperation<UpdateCollaboratorsCmSheetCourseItem>[];

export type UpdateCmSheetCertificateOfCoursesRequest = { file: File };
export interface CollaboratorCmSheetCertification {
  id: number;
  certification: Certification;
  deliveryDate: string | null;
  expirationDate: string | null;
  fileName: string;
  status: string | null;
}
export type GetCollaboratorsCmSheetCertificationsResponse =
  CollaboratorCmSheetCertification[];

export type UpdateCollaboratorsCertificationItem = {
  certificationId: number;
  deliveryDate: string | null;
  expirationDate: string | null;
};
export type UpdateCollaboratorsCmSheetCertificationsRequest =
  BatchOperation<UpdateCollaboratorsCertificationItem>[];

export type CollaboratorHistoryCourse = {
  course: HistoryCourse;
  id: number;
  attendanceYear: number;
  external: boolean;
  fileName: string;
  verificationStatus: VerificationStatusEnum;
  fromCmSheet: boolean;
};

export type GetCollaboratorsHistoryCoursesResponse =
  PaginatedBody<CollaboratorHistoryCourse>;

export type AddCollaboratorsHistoryCourse = {
  courseId: number | null;
  courseName: string;
  attendanceYear: number;
  external: boolean;
};

export type UpdateCollaboratorsHistoryCourse = {
  courseName?: string;
  attendanceYear?: number;
};

export interface CollaboratorHistoryCertification {
  certification: HistoryCertification;
  id: number;
  status: string;
  deliveryDate: string;
  expirationDate: string | null;
  external: boolean;
  fileName: string;
  verificationStatus: VerificationStatusEnum;
  fromCmSheet: boolean;
}
export type GetCollaboratorsHistoryCertificationsResponse =
  PaginatedBody<CollaboratorHistoryCertification>;

export type AddCollaboratorsHistoryCertification = {
  certificationId: number | null;
  certificationName: string;
  deliveryDate: string | null;
  expirationDate: string | null;
  external: boolean;
};

export type UpdateCollaboratorsHistoryCertification = {
  certificationName: string;
  deliveryDate: string;
  expirationDate: string | null;
};

export type GetCollaboratorResumeResponse = CollaboratorResume[];
export type InternalGradeData = GetCollaboratorCmSheetInternalGradeResponse;

export type CollaboratorResume = {
  collaboratorDetail: UserDetail;
  currentProjects: string[];
  cmSheetGoals: GetCollaboratorCmSheetGoalsResponse;
  professionalWay: CollaboratorCmSheetCareerPath;
  businessAreaManagerComment: string | null;
  finalInterview: CollaboratorsCmSheetFinalInterview;
  collaboratorIncreasings: CollaboratorIncreasing;
  collaboratorFeedback: CollaboratorFeedback | null;
  internalGrade: InternalGradeData;
  year: number;
};

export type UpdateCollaboratorResumeRequest = {
  indicators: CollaboratorsCmSheetFinalInterviewDevelopmentIndicators[];
  collaboratorIncreasings: Omit<
    CollaboratorIncreasing,
    "isInternalGradeIncreaseApplicable"
  >;
  endInternalGrade: InternalGrade;
};

export type UserDetail = {
  area: string;
  practice: string;
  cc: string;
  totalExperienceMonths: number | null;
  companyExperienceMonths: number | null;
};

export type Goals = {
  description: string;
  weighting: number;
};

export type CollaboratorFeedback = {
  levelId: number;
  collaboratorComment: string;
};

export enum ScreenEnum {
  A = "A",
  B = "B",
  C = "C",
  D = "D",
}
export type CollaboratorCv = {
  experienceYears: number | null;
  companyYears: number | null;
  cvFileName: string;
  linkedinUrl: string;
};

export type GetCollaboratorCvResponse = CollaboratorCv;

export type UpdateCollaboratorCvRequest = {
  experienceYears?: number;
  companyYears?: number;
  cvFile?: File | string;
  linkedinUrl?: string;
};

export type CollaboratorCmSheetSoftSkill = {
  dimensionId: number;
  levelId: number;
};

export type GetCollaboratorCmSheetSoftSkillsResponse =
  CollaboratorCmSheetSoftSkill[];

export type UpdateCollaboratorCmSheetSoftSkillsRequest =
  CollaboratorCmSheetSoftSkill[];

export type CollaboratorCmSheetGoal = {
  id: number;
  goal: string;
  timeChallenge: number;
  contestChallenge: number;
  actionChallenge: number;
  impact: number;
  achievementPercent: number;
};

export type GetCollaboratorCmSheetGoalsResponse = {
  goals: CollaboratorCmSheetGoal[];
  proposedDimensionPercentage: number | null;
  finalDimensionPercentage: number | null;
  totalImpact: number | null;
};

export type UpdateCollaboratorCmSheetGoalsRequest =
  BatchOperation<CollaboratorCmSheetGoal>[];

export type CollaboratorCmSheetCareerPath = {
  developmentGoalFocus: string;
  actionPlan: string;
};

export type GetCollaboratorCmSheetCareerPathResponse =
  CollaboratorCmSheetCareerPath;

export type UpdateCollaboratorCmSheetCareerPathRequest =
  CollaboratorCmSheetCareerPath;

export type AdminFeedback = {
  developmentComment: string;
  performanceComment: string;
  isDevelopmentCommentSent: boolean;
};

export type GetCmSheetAdminFeedbacksResponse = AdminFeedback;

export type UpdateCmSheetAdminFeedbacksRequest = Partial<
  Omit<AdminFeedback, "isDevelopmentCommentSent">
>;

export type CmSheetFeedbacksCollaborator = {
  collaboratorComment: string;
  satisfactionLevelId: number | null;
};

export type GetCmSheetFeedbacksCollaboratorResponse =
  CmSheetFeedbacksCollaborator;

export type UpdateCmSheetFeedbacksCollaboratorRequest =
  CmSheetFeedbacksCollaborator;

export type CollaboratorsCmSheetNote = {
  id: number;
  collaboratorId: number;
  collaboratorName: string;
  collaboratorSurname: string;
  collaboratorRole: Role;
  createdAt: string;
  text: string;
};

export type GetCollaboratorsCmSheetNotesResponse = CollaboratorsCmSheetNote[];

export type UpdateCollaboratorsCmSheetNotesRequest = BatchOperation<{
  text: string;
}>[];

export type CollaboratorsCmSheetFinalInterviewDevelopmentIndicators = {
  microCriterio: string;
  total: number;
};

export type CollaboratorsCmSheetFinalInterviewDevelopmentScreen = {
  screen: ScreenEnum;
  description: string;
  range: [number, number];
  indicators: CollaboratorsCmSheetFinalInterviewDevelopmentIndicators[];
  totalIndicators: number;
};

export type CollaboratorsCmSheetFinalInterview = {
  developmentScreen: CollaboratorsCmSheetFinalInterviewDevelopmentScreen | null;
  feedbackToCollaborator: string | null;
  dimensionId: number | null;
  dimensionPercentage: number | null;
  totalImpact: number | null;
  proposedComment: string | null;
};

export type GetCollaboratorsCmSheetFinalInterviewResponse =
  CollaboratorsCmSheetFinalInterview;

export type UpdateCollaboratorsCmSheetFinalInterviewRequest = {
  proposedComment: string;
};

export type UpdateCertificateOfCertificationsRequest = { file: File };

export type UpdateCertificateOfCoursesRequest = { file: File };

export type AddCollaboratorsHistoryCertificationResponse = { id: number };

export type AddCollaboratorsHistoryCourseResponse = { id: number };

export type GetCmSheetAvailableCertificationsResponse =
  PaginatedBody<Certification>;

export type UpdateCmSheetCertificateOfCertificationsRequest = { file: File };

export type CmSheetValidationSection = {
  careerPath: boolean | null;
  softSkills: boolean | null;
  collaboratorFeedback: boolean | null;
  internalGrade: boolean | null;
  finalInterview: boolean | null;
  goals: boolean | null;
  development: boolean | null;
};

export type CmSheetStateMachine = {
  currentState: CmSheetStateEnum;
  previousState: CmSheetStateEnum | null;
  possibleEvents: CmSheetEventEnum[];
  isRevisionRequestSent: boolean;
  isRevisionConfirmed: boolean;
  isCMSheetVisibleToCollaborator: boolean;
  sendCMSheetValidationSection: CmSheetValidationSection;
  changeCMSheetStatusValidationSection: CmSheetValidationSection;
};

export type GetCmSheetStateMachineResponse = CmSheetStateMachine;

export type UpdateCmSheetStateMachinewRequest = { event: CmSheetEventEnum };

export type CollaboratorsHistorySkillLanguages = {
  id: number;
  language: string;
  listeningComprehension: LanguageLevelEnum;
  readingComprehension: LanguageLevelEnum;
  spokenInteraction: LanguageLevelEnum;
  spokenOralProduction: LanguageLevelEnum;
  written: LanguageLevelEnum;
  nativeLanguage: boolean;
  collaboratorId: number;
  verificationStatus: VerificationStatusEnum;
};

export type GetCollaboratorsHistorySkillLanguagesResponse =
  CollaboratorsHistorySkillLanguages[];

export type AddCollaboratorsHistorySkillLanguages = {
  language: string;
  nativeLanguage: boolean;
  listeningComprehension: LanguageLevelEnum;
  readingComprehension: LanguageLevelEnum;
  spokenInteraction: LanguageLevelEnum;
  spokenOralProduction: LanguageLevelEnum;
  written: LanguageLevelEnum;
};

export type UpdateCollaboratorsHistorySkillLanguages = Partial<
  Omit<AddCollaboratorsHistorySkillLanguages, "language">
>;

export type CollaboratorsHistoryTechSkill = {
  id: number;
  level: string;
  competence: string;
  stream: string;
  software: string;
  component: string;
  description: string;
  verificationStatus: VerificationStatusEnum;
};

export type GetCollaboratorsHistoryTechSkillsResponse =
  PaginatedBody<CollaboratorsHistoryTechSkill>;

export type AddCollaboratorsHistoryTechSkillRequest = {
  techSkillId: number;
  level: string;
};

export type AddCollaboratorsHistoryTechSkillRequestList =
  AddCollaboratorsHistoryTechSkillRequest[];

export type UpdateCollaboratorsHistoryTechSkillRequest = {
  level: string;
};

export type GetCollaboratorsHistoryTechSkillAvailableResponse =
  PaginatedBody<TechSkill>;

export type ImpersonateCollaborators = {
  id: number;
  email: string;
  name: string;
  surname: string;
  role: Role;
};

export type GetImpersonateCollaboratorsResponse =
  PaginatedBody<ImpersonateCollaborators>;

export type CollaboratorHistoryProfessionalExperience = {
  id: number;
  companyName: string;
  startDate: string;
  endDate: string | null;
  isAbstract: boolean;
  verificationStatus: VerificationStatusEnum;
};

export type GetCollaboratorsHistoryProfessionalExperiencesResponse =
  PaginatedBody<CollaboratorHistoryProfessionalExperience>;

export type SubmitCollaboratorsHistoryProfessionalExperienceRequest = Omit<
  CollaboratorHistoryProfessionalExperience,
  "id" | "verificationStatus"
>;

export type AddCollaboratorsHistoryProfessionalExperienceResponse = {
  id: number;
};

export type AddUpdateCollaboratorsEducation = {
  qualification: string;
  institution: string;
  studyPath: string;
  startDate: string;
  endDate: string | null;
};

export type CollaboratorHistoryEducation = {
  qualification: string;
  institution: string;
  studyPath: string;
  startDate: string;
  endDate: string;
  id: number;
  verificationStatus: VerificationStatusEnum;
};

export type GetCollaboratorsHistoryEducationsResponse =
  PaginatedBody<CollaboratorHistoryEducation>;

export type AddCollaboratorsHistoryEducationResponse = { id: number };
export type CollaboratorHistoryProfessionalData = {
  urls: {
    id: number;
    linkedInUrl: string;
  }[];
  monthsOfExperienceTotal: number | null;
  monthsOfExperienceAbstract: number | null;
};

export type GetCollaboratorsHistoryProfessionalDataResponse =
  CollaboratorHistoryProfessionalData;

export type AddCollaboratorsHistoryProfessionalDataRequest = {
  linkedInUrl: string;
};

export type UpdateCollaboratorsHistoryProfessionalDataRequest = {
  linkedInUrl: string;
};

export type CollaboratorSectionCounter = {
  draft: number;
  pending: number;
  verified: number;
};

export type CollaboratorHistoryApprovalStatus = {
  totalSectionsCounts: CollaboratorSectionCounter;
  experienceCounts: CollaboratorSectionCounter;
  languageSkillCounts: CollaboratorSectionCounter;
  techSkillCounts: CollaboratorSectionCounter;
  educationCounts: CollaboratorSectionCounter;
  courseCounts: CollaboratorSectionCounter;
  certificationCounts: CollaboratorSectionCounter;
  projectCounts: CollaboratorSectionCounter;
  mostRecentCVGenerationDate: string | null;
};

export type GetCollaboratorsHistoryApprovalFlowResponse =
  CollaboratorHistoryApprovalStatus;

export type GetCollaboratorCmSheetDevelopmentCommentsResponse = {
  cmComment: string;
  businessAreaManagerComment: string;
  careerDevelopmentCommitteeNote: string;
  collaboratorIncreasings: CollaboratorIncreasing | null;
  feedbackToCollaborator: string;
  isNoteCareerDevelopmentCommitteeSent: boolean;
};

export type UpdateCollaboratorCmSheetDevelopmentCommentsRequest = {
  cmComment: string | null;
  businessAreaManagerComment: string | null;
  feedbackToCollaborator: string | null;
  collaboratorIncreasings: Omit<
    CollaboratorIncreasing,
    "isInternalGradeIncreaseApplicable"
  > | null;
  careerDevelopmentCommitteeNote: string | null;
};

export enum CollaboratorIncreasingEnum {
  YES = "YES",
  NO = "NO",
  ANALYSIS_REQUIRED = "ANALYSIS_REQUIRED",
}

export type CollaboratorIncreasing = {
  internalGradeIncrease: CollaboratorIncreasingEnum | null;
  ccnlIncrease: CollaboratorIncreasingEnum | null;
  salaryIncrease: CollaboratorIncreasingEnum | null;
  isInternalGradeIncreaseApplicable: boolean | null;
};

export type DevelopmentIndicatorItem = {
  developmentId: number;
  developmentValue: number | null;
};

export type CollaboratorCmSheetDevelopmentIndicators = {
  completedActivities: number;
  indicators: DevelopmentIndicatorItem[];
};

export type GetCollaboratorCmSheetDevelopmentIndicatorsResponse =
  CollaboratorCmSheetDevelopmentIndicators;

export type UpdateCollaboratorCmSheetDevelopmentIndicatorsRequest =
  DevelopmentIndicatorItem[];

export type internalGradeType = {
  jobFamilies: string | null | undefined;
  grade: string | null | undefined;
  path: string | null;
  subGrade: string | null | undefined;
};
